/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import useSWR from "swr"
import { useRouter } from "next/router"
import { useAuthReady } from "./useAuthReady"
import {
  Item,
  ResponseItemGetOne,
  ResponseItemsGetList,
} from "../libs/api/backend/src"
import { UrlItemFindListAPI, UrlItemGetOneAPI } from "../libs/api/url"

export function useItemId(): string {
  let itemId = ""

  try {
    const router = useRouter()
    itemId = (router.query.itemId as string) || ""
  } catch (err) {
    console.error(err)
  }

  return itemId
}

export function useItemSearchText(): string {
  let searchText = ""

  try {
    const router = useRouter()
    searchText = (router.query.searchText as string) || ""
  } catch (err) {
    console.error(err)
  }

  return searchText
}

export function useItemFindOne(enterpriseId?: string, itemId?: string) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseItemGetOne>(
    isAuthenticated && enterpriseId && itemId
      ? UrlItemGetOneAPI(enterpriseId, itemId)
      : null
  )

  const item = apiResponse && (apiResponse.data as Item)

  return {
    item: apiResponse && item ? item : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterpriseId && itemId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useItemFindAll(
  enterpriseId?: string,
  {
    shopId,
    categoryId,
    searchText,
    limit,
    offset,
  }: {
    shopId?: string
    categoryId?: string
    searchText?: string
    limit?: number
    offset?: number
  } = {}
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseItemsGetList>(
    isAuthenticated && enterpriseId
      ? UrlItemFindListAPI(enterpriseId, {
          shopId,
          categoryId,
          searchText,
          limit,
          offset,
        })
      : null
  )

  const items = apiResponse && (apiResponse.data as Array<Item>)

  return {
    items: apiResponse?.data ?? null,
    isLoading: Boolean(
      authLoading || (isAuthenticated && enterpriseId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useItemSearch(
  enterpriseId?: string,
  searchText?: string,
  {
    shopId,
    categoryId,
  }: {
    shopId?: string
    categoryId?: string
  } = {}
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseItemsGetList>(
    isAuthenticated && enterpriseId && searchText
      ? UrlItemFindListAPI(enterpriseId, { shopId, categoryId, searchText })
      : null
  )

  const items = apiResponse && (apiResponse.data as Array<Item>)

  return {
    items: apiResponse?.data ?? null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterpriseId && searchText && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}
